import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useLoggedEffect } from 'hooks/useLoggedEffect';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { SOURCE_QUERY_PARAM } from 'pages/validate-3ds-payment';
import { useEffect, useRef } from 'react';
import { Message } from 'semantic-ui-react';
import { trackEecCheckoutOption } from 'utils/analytics/analytics';
import AdyenDropInCheckout from './AdyenDropInCheckout';

export const AdyenCheckoutWrapper = observer(() => {
  const router = useRouter();

  const { numberSetup } = useDrawStore();
  const {
    orderLoaded,
    isReferralFlow,
    orderId,
    isValid,
    haveValidAdyenPaymentSession,
    adyenPaymentSession,
    confirmed,
    checked_user_referral,
    upgradePageSource,
    setOrderError,
    orderError,
    navigateToCheckout,
    checkOrderStatusAndAdvanceToConfirmation,
    maybeContinueToPayment,
    currentPromo,
    initialised_promocode,
  } = useEnterStore();

  const {
    initialized: userStoreInitialised,
    eligibleForIntroOffer,
    currentUser,
  } = useUserStore();
  // Holds a reference to the timer that periodically checks for order completion.
  // Needed so we can clear it when the component unmounts.

  const orderIdRef = useRef<string | null>(orderId);
  const orderCompletionTimerRef = useRef<any>(null);
  const tenMinutes = 10 * 60 * 1000;

  useEffect(() => {
    return () => {
      if (orderCompletionTimerRef.current) {
        clearTimeout(orderCompletionTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    orderIdRef.current = orderId;
  }, [orderId]);

  useEffect(() => {
    if (haveValidAdyenPaymentSession && !orderCompletionTimerRef.current) {
      // Start periodically checking whether order is complete.
      clearTimeout(orderCompletionTimerRef.current);
      orderCompletionTimerRef.current = null;
      checkOrderStatusAndAdvanceToConfirmation(
        orderIdRef,
        orderCompletionTimerRef,
      );
    }
  }, [
    haveValidAdyenPaymentSession,
    orderIdRef.current,
    // We only want the useEffect to re-run if the timer's ID changes between
    // falsey and truthy, not every time the timer's ID changes
    !!orderCompletionTimerRef.current,
  ]);

  useEffect(() => {
    (async () => {
      if (!isValid) {
        router.replace(isReferralFlow ? '/enter-now' : '/enternow/1');
      } else {
        if (userStoreInitialised && !currentUser) {
          await navigateToCheckout(isReferralFlow);
        }
      }
    })();
  }, [isValid, userStoreInitialised, currentUser, router, isReferralFlow]);

  useLoggedEffect(
    'AdyenCheckoutWrapper continue to payment',
    () => {
      if (
        !haveValidAdyenPaymentSession &&
        isValid &&
        currentUser &&
        initialised_promocode
      ) {
        maybeContinueToPayment();
      }
    },
    [
      haveValidAdyenPaymentSession,
      isValid,
      currentUser,
      orderId,
      currentPromo || false,
      initialised_promocode,
    ],
  );

  if (!isValid) {
    return <Message error>Order not valid</Message>;
  }
  if (!confirmed) {
    return <Message error>Order not confirmed</Message>;
  }

  if (!haveValidAdyenPaymentSession || !adyenPaymentSession) {
    return (
      <>
        {orderError && orderError.length > 0 && (
          <div style={{ margin: '10px' }}>
            <div style={{ height: '20px' }} />
            <Message
              id="errorMessage"
              negative
              style={{ width: '100%', maxWidth: '500px', fontSize: '12px' }}
            >
              {orderError}
            </Message>
          </div>
        )}
        {!orderError ||
          (orderError.length <= 0 && (
            <PlainLoadingSpinner style={{ margin: '2em auto' }} />
          ))}
      </>
    );
  }

  return (
    <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
      {orderError && orderError.length > 0 && (
        <div style={{ margin: '10px' }}>
          <>
            <div style={{ height: '20px' }} />
            <Message
              id="errorMessage"
              negative
              style={{ width: '100%', maxWidth: '500px', fontSize: '12px' }}
            >
              {orderError}
            </Message>
          </>
        </div>
      )}
      {orderLoaded && adyenPaymentSession && (
        <AdyenDropInCheckout
          key={adyenPaymentSession?.adyenPaymentSessionId || 'unknownsession'}
          queryParamsObject={{
            source: upgradePageSource
              ? SOURCE_QUERY_PARAM.UPGRADE
              : SOURCE_QUERY_PARAM.STANDARD_TICKET_ORDER,
          }}
          paymentSession={adyenPaymentSession}
          onLoadingStateChange={(loading: boolean) => {
            // Handle loading state if needed
          }}
          completionHandler={async () => {
            await trackEecCheckoutOption('Card');
          }}
          displayError={async (error: string | null) => {
            setOrderError(error);
          }}
          style={{
            fontSize: '12px',
            width: '100%',
            maxWidth: 500,
          }}
          initialRedirectResult={undefined}
        />
      )}
    </div>
  );
});
